import { withLocalePrefix } from '../providers/locale-aware-router'
import { escapeHtmlSpecialChars } from './html-utils'

// Lexical TextNode format bitmask (see @lexical/rich-text FORMAT_TEXT_COMMAND).
const IS_BOLD = 1
const IS_ITALIC = 1 << 1
const IS_STRIKETHROUGH = 1 << 2
const IS_UNDERLINE = 1 << 3

interface LexicalLinkFields {
  url?: string
  newTab?: boolean
}

interface LexicalNode {
  type?: string
  tag?: string
  format?: number
  text?: string
  children?: LexicalNode[]
  fields?: LexicalLinkFields
  url?: string
}

// Only allow schemes/forms that can't execute script (blocks javascript:,
// data:, vbscript:, etc.) in CMS-authored link hrefs.
const SAFE_HREF_PATTERN = /^(https?:|mailto:|tel:|\/|#)/i

function sanitizeHref(url: string | undefined): string | null {
  const trimmed = url?.trim()
  return trimmed && SAFE_HREF_PATTERN.test(trimmed) ? trimmed : null
}

function renderChildren(
  children: LexicalNode[] | undefined,
  currentUrl: string
): string {
  return (children ?? []).map(node => renderNode(node, currentUrl)).join('')
}

function renderText(node: LexicalNode): string {
  let html = escapeHtmlSpecialChars(node.text ?? '')
  const format = node.format ?? 0

  if (format & IS_BOLD) html = `<strong>${html}</strong>`
  if (format & IS_ITALIC) html = `<em>${html}</em>`
  if (format & IS_UNDERLINE) html = `<u>${html}</u>`
  if (format & IS_STRIKETHROUGH) html = `<s>${html}</s>`

  return html
}

const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'])
const LIST_TAGS = new Set(['ul', 'ol'])

function renderLink(node: LexicalNode, currentUrl: string): string {
  const children = renderChildren(node.children, currentUrl)
  const href = sanitizeHref(node.fields?.url ?? node.url)

  if (!href) {
    // Unsafe/missing href: still render the text so content isn't lost.
    return children
  }

  // Only internal, root-relative links need the locale prefix - raw
  // <a href> markup never goes through LocaleAwareRouter, so it has to be
  // baked in here. External/mailto/tel links are left untouched.
  const finalHref = href.startsWith('/')
    ? withLocalePrefix(currentUrl, href)
    : href

  const target = node.fields?.newTab
    ? ' target="_blank" rel="noopener noreferrer"'
    : ''

  return `<a href="${escapeHtmlSpecialChars(finalHref)}"${target}>${children}</a>`
}

function renderNode(node: LexicalNode, currentUrl: string): string {
  switch (node.type) {
    case 'root':
      return renderChildren(node.children, currentUrl)
    case 'paragraph':
      return `<p>${renderChildren(node.children, currentUrl)}</p>`
    case 'heading': {
      const tag = node.tag && HEADING_TAGS.has(node.tag) ? node.tag : 'h6'
      return `<${tag}>${renderChildren(node.children, currentUrl)}</${tag}>`
    }
    case 'list': {
      const tag = node.tag && LIST_TAGS.has(node.tag) ? node.tag : 'ul'
      return `<${tag}>${renderChildren(node.children, currentUrl)}</${tag}>`
    }
    case 'listitem':
      return `<li>${renderChildren(node.children, currentUrl)}</li>`
    case 'linebreak':
      return '<br>'
    case 'text':
      return renderText(node)
    case 'link':
    case 'autolink':
      return renderLink(node, currentUrl)
    default:
      // Unknown node types (e.g. not yet supported by this converter) still
      // render their children so partially-supported content degrades
      // gracefully instead of silently dropping text.
      return renderChildren(node.children, currentUrl)
  }
}

/**
 * Converts a Lexical rich-text JSON document (as returned by the Payload CMS
 * `Page.content` field) into an HTML string. Pure string transform with no DOM
 * dependency, so it produces identical output during SSR and in the browser.
 *
 * `currentUrl` (e.g. `router.url`) is used to prefix root-relative internal
 * links with /en when needed, since the raw `<a href>` markup this produces
 * never goes through LocaleAwareRouter.
 */
export function lexicalJsonToHtml(content: unknown, currentUrl = ''): string {
  const root = (content as { root?: LexicalNode } | null | undefined)?.root

  if (!root || typeof root !== 'object') {
    return ''
  }

  return renderNode(root, currentUrl)
}
